// Shared hit-list persistence (v0.7). Same offline-first shape as booths: every
// star/un-star is a local IndexedDB write that lands instantly, flips the record to
// 'local', and gets drained to the backend by the sync engine when there's signal.
//
// Un-star = tombstone, exactly as booth delete does it. A hard delete would be
// resurrected by the next pull, so removals have to travel as records.

import { getDB } from './database';
import type { HitListTarget } from './schema';
import type { Show } from '../config';

/** Every target on this device, INCLUDING tombstones (callers filter). */
export async function listAllHitList(): Promise<HitListTarget[]> {
  const db = await getDB();
  return db.getAll('hitlist');
}

/** Live (starred) targets only, newest star first. */
export async function listHitList(showKey?: string): Promise<HitListTarget[]> {
  const all = await listAllHitList();
  return all
    .filter((t) => !t.deleted && (!showKey || t.show_key === showKey))
    .sort((a, b) => b.added_at.localeCompare(a.added_at));
}

/**
 * The set of keys currently starred, for the directory's ★ state. Returns BOTH
 * identity domains (exhibitor_key and name_key) in one set — the caller tests an
 * exhibitor's two keys against it, so a star still matches after a data re-pull
 * changes one of them. See exhibitorKey() in src/exhibitors/data.ts.
 */
export async function starredKeys(): Promise<Set<string>> {
  const live = await listHitList();
  const set = new Set<string>();
  for (const t of live) {
    set.add(t.exhibitor_key);
    if (t.name_key) set.add(t.name_key);
  }
  return set;
}

export interface StarInput {
  exhibitor_key: string;
  name_key: string;
  show: Show;
  show_key: string;
  name: string;
  booths: string[];
  added_by: string;
}

/**
 * Star an exhibitor. Re-starring something previously un-starred REVIVES the
 * existing row (clears the tombstone, bumps updated_at) rather than writing a new
 * one — the row's key is the merge identity, so reviving is what makes the un-star
 * → re-star round trip converge on other devices.
 */
export async function star(input: StarInput): Promise<HitListTarget> {
  const db = await getDB();
  const now = new Date().toISOString();
  const existing = await db.get('hitlist', input.exhibitor_key);

  const target: HitListTarget = {
    ...input,
    id: existing?.id ?? crypto.randomUUID(),
    // Keep the ORIGINAL star time across a revive; it's the team's "when did this
    // land on the plan" and shouldn't reset because someone toggled it twice.
    added_at: existing?.added_at ?? now,
    updated_at: now,
    deleted: false,
    sync_status: 'local',
  };
  await db.put('hitlist', target);
  return target;
}

/** Un-star: tombstone the row so the removal propagates. No-op if never starred. */
export async function unstar(exhibitorKey: string): Promise<void> {
  const db = await getDB();
  const existing = await db.get('hitlist', exhibitorKey);
  if (!existing) return;
  existing.deleted = true;
  existing.updated_at = new Date().toISOString();
  existing.sync_status = 'local';
  await db.put('hitlist', existing);
}

/** Targets not yet pushed (includes tombstones — removals must propagate too). */
export async function listUnsyncedHitList(): Promise<HitListTarget[]> {
  const db = await getDB();
  return db.getAllFromIndex('hitlist', 'sync_status', 'local');
}

export async function markHitListSynced(exhibitorKey: string): Promise<void> {
  const db = await getDB();
  const t = await db.get('hitlist', exhibitorKey);
  if (!t) return;
  t.sync_status = 'synced';
  await db.put('hitlist', t);
}

/**
 * Merge pulled targets, last-write-wins by `updated_at` — the booth merge rule,
 * applied to stars. A remote row only wins when it's strictly newer, so an un-star
 * this device hasn't pushed yet is never clobbered by the stale star it replaced.
 * Tombstones merge like any other record; the read paths hide them.
 */
export async function mergeRemoteHitList(remote: HitListTarget[]): Promise<number> {
  const db = await getDB();
  let upserted = 0;
  for (const r of remote) {
    if (!r?.exhibitor_key) continue;
    const local = await db.get('hitlist', r.exhibitor_key);
    if (local && local.updated_at >= r.updated_at) continue;
    await db.put('hitlist', { ...r, sync_status: 'synced' });
    upserted++;
  }
  return upserted;
}
